import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import { useWindowSize } from '@brands/hooks';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Modal, { Styles } from 'react-modal';

import { COMPLIANCE_CHECKLIST_ITEMS, getComplianceAttestationLabel } from '../../utils/complianceChecklistCopy';
import styles from './styles.module.scss';
import { CASE_TYPE_LABELS, CASE_TYPE_OPTIONS, complianceChecklistSchema } from './validation';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmitChecklist: (data: any) => void;
  originalCaseType: string;
  providerName: string;
}

export type ComplianceFormValues = {
  confirmCaseType: string;
  reason?: string;
  otherReason?: string;
  additionalPeople?: string;
  confirmChecked: boolean;
};

const REASON_OPTIONS = [
  'Patient unable to connect to live video',
  'Platform or system outage',
  'Patient requested asynchronous review instead',
  'Patient unavailable at scheduled time',
  'Other',
].map((item) => ({
  value: item,
  label: item,
}));

export default function ComplianceChecklistModal({
  isOpen,
  onClose,
  onSubmitChecklist,
  originalCaseType,
  providerName,
}: Props) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useForm<ComplianceFormValues>({
    mode: 'onChange',
    resolver: yupResolver(complianceChecklistSchema(originalCaseType)),
    defaultValues: {
      confirmCaseType: originalCaseType,
      reason: '',
      otherReason: '',
      additionalPeople: '',
      confirmChecked: false,
    },
    shouldUnregister: false,
  });

  const selectedCaseType = watch('confirmCaseType');
  const selectedReason = watch('reason');

  const caseTypeChanged = selectedCaseType !== originalCaseType;

  const screenSize = useWindowSize();
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);

  useEffect(() => {
    if (isOpen) {
      reset({
        confirmCaseType: originalCaseType,
        reason: '',
        otherReason: '',
        additionalPeople: '',
        confirmChecked: false,
      });
    }
  }, [isOpen, originalCaseType, reset]);

  const submitHandler = (data: ComplianceFormValues) => {
    const payload = {
      ...data,
      originalCaseType,
      providerName,
      timestamp: new Date(),
    };

    onSubmitChecklist(payload);
  };

  const modalStyles: Styles = isMobile
    ? {
        ...customStylesDashboard,
        overlay: {
          ...(customStylesDashboard?.overlay || {}),
          overflowY: 'scroll',
          overflowX: 'hidden',
        },
        content: {
          ...(customStylesDashboard?.content || {}),
          width: '100vw',
          height: '100vh',
          position: 'fixed',
          top: '50%',
          left: '50%',
          padding: '90px 16px 32px 16px',
          transform: 'translate(-50%, -50%)',
          right: '0',
          bottom: '0',
          margin: '0',
          borderRadius: '0',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          overflowY: 'scroll',
        },
      }
    : {
        ...customStylesDashboard,
        content: {
          ...customStylesDashboard.content,
          width: '444px',
          maxHeight: '744px',
          overflowY: 'auto',
        },
      };

  useEffect(() => {
    trigger(['confirmCaseType', 'reason', 'otherReason']);
  }, [selectedCaseType, selectedReason, trigger]);

  useEffect(() => {
    if (watch('otherReason')?.trim() !== '') {
      setValue('otherReason', watch('otherReason'), { shouldValidate: true });
    }
  }, [watch('otherReason')]);

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} ariaHideApp={false} style={modalStyles}>
      <Box p={3} className={`${styles.body} ${styles.formContainer}`}>
        <div className={styles.bodyHead}>
          <CloseIcon onClick={onClose} />
        </div>

        <Typography variant="h5" fontWeight={600} mb={3}>
          Compliance checklist
        </Typography>

        <div className={styles.complianceConfirmCaseType}>
          <MUISelectInput
            name="confirmCaseType"
            label="Confirm case type"
            options={CASE_TYPE_OPTIONS}
            value={selectedCaseType || null}
            required
            errors={errors}
            onChange={(value: string) =>
              setValue('confirmCaseType', value, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }
            inputClass={`${styles.profileInputClass} fs-exclude`}
            wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
          />

          {caseTypeChanged && (
            <>
              <MUISelectInput
                name="reason"
                label="Reason for case change"
                options={REASON_OPTIONS}
                value={selectedReason || null}
                required
                errors={errors}
                onChange={(value: string) =>
                  setValue('reason', value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
              />

              {selectedReason === 'Other' && (
                <TextField
                  label="Provide additional details"
                  {...register('otherReason')}
                  onChange={(e: any) => {
                    setValue('otherReason', e.target.value);
                  }}
                  required
                  errors={errors.otherReason ? errors : {}}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
                />
              )}
            </>
          )}
        </div>

        <Box mt={3} mb={2}>
          <ul className={styles.caseDetailsList}>
            {COMPLIANCE_CHECKLIST_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </Box>

        <TextField
          label="Who?"
          {...register('additionalPeople')}
          errors={errors.additionalPeople ? errors : {}}
          onChange={(e: any) => {
            setValue('additionalPeople', e.target.value);
          }}
          inputClass={`${styles.profileInputClass} fs-exclude`}
          wrapperClassName={`fs-unmask ${styles.completeProfileClass}`}
        />

        {/* Confirmation */}
        <FormControlLabel
          className={styles.confirmChecked}
          control={
            <Checkbox
              {...register('confirmChecked', {
                required: 'You must confirm before submitting',
              })}
            />
          }
          label={getComplianceAttestationLabel(providerName)}
        />

        <Box mt={3}>
          <Button
            fullWidth
            className={styles.checklistBtn}
            variant="contained"
            disabled={!isValid}
            onClick={handleSubmit(submitHandler)}
            sx={{ mb: 2 }}
          >
            Confirm
          </Button>

          <Button fullWidth variant="text" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
